// src/StrategyPage.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  motion,
  AnimatePresence,
  useReducedMotion,
  useScroll,
  useSpring,
} from "framer-motion";

const STRATEGY_COPY = {
  en: {
    codeLabel: "EN",
    headerSubtitle: "Sweet Trading · Strategy",
    langButtonLabel: "Language",
    backLabel: "Back",

    heroBadge: "Strategy Deep Dive",
    heroTitle: "How Candy Bot trades — simple explanation of a complex system",
    heroIntro:
      "Candy Bot is built around a clear idea: automate a technical strategy and surround it with strict risk rules. On this page we explain, in simple words, how the core logic works, how the grid behaves, and which protections are used to keep the risk under control.",
    heroChips: [
      "Indicator-based entries",
      "Controlled grid logic",
      "Prop-style risk rules",
    ],

    sideBadge: "Profile at a glance",
    sideTitle: "TRIX + Grid with Risk Framework",
    sideP1:
      "The strategy combines a TRIX-based entry signal with a small and limited grid used to improve the average entry price when the market temporarily moves against the initial position. All of this is wrapped inside fixed protection rules inspired by prop firms (equity limits, daily limits, global stops).",
    sideP2:
      "Numbers and exact parameters shown here are illustrative and simplified on purpose. The live configuration can be adjusted and optimised over time.",

    s1Title: "1. Core trading logic: TRIX + Grid",
    s1Intro:
      "At the heart of the strategy there is a simple structure: the TRIX indicator decides the direction, and a small grid manages the position if price moves temporarily in the opposite direction.",

    trixTitle: "TRIX: the main entry trigger",
    trixP1:
      "TRIX is a technical indicator that measures how fast the market is moving and in which direction. To keep it simple:",
    trixBullets: [
      "When TRIX is clearly positive, the bot considers the market bullish and prepares buy setups.",
      "When TRIX is clearly negative, the bot considers the market bearish and prepares sell setups.",
    ],
    trixP2:
      "The strategy only opens a new position when no other trades are open and the TRIX reading crosses specific thresholds that indicate a meaningful move, not just random noise.",

    gridTitle: "The grid: controlled averaging, not infinite martingale",
    gridP1:
      'Once the first trade is open, the bot can create a "basket" of trades, also called a grid. The idea is:',
    gridBullets: [
      "Start with a small base volume (for example 0.01 lots or similar, depending on account size).",
      "If price moves against the position by a predefined distance, the bot can add a new trade, improving the average entry price.",
      "The distance between one trade and the next is based on a minimum pip distance combined with volatility filters (for example, using ATR to avoid placing trades too close in very calm markets).",
      "The volume of each new trade can slightly increase (multiplier < 2), but there is a hard cap on how many trades can be opened in the basket.",
    ],
    gridP2:
      "The basket has its own take profit: when the total floating profit of the whole basket reaches a predefined amount (calculated in a way that depends also on the total volume), the entire basket is closed together.",

    safetyTitle: "Safety filters before adding any new trade",
    safetyP1:
      "The bot does not add trades blindly. Before opening a new position inside the basket, it checks multiple conditions, for example:",
    safetyBullets: [
      "A minimum time delay since the last trade, so that trades are not stacked too fast.",
      "Price must have moved enough in the opposite direction (based on the grid distance discussed above).",
      "Filters on volatility spikes (for example using ATR): if the candle is excessively large, the bot can decide to wait before adding any new trade.",
    ],
    safetyP2:
      "If conditions are not optimal, the bot simply waits. The goal is to keep the grid relatively small and controlled, not to chase every price tick.",

    s2Title: "2. Risk management: inspired by prop firm rules",
    s2Intro:
      "The strategy is designed to behave like a professional trader in a prop firm: there are clear limits that cannot be exceeded. These limits are more important than the single trade or the single day.",

    basketTitle: "Basket stop & account equity stop",
    basketP1:
      "Every grid basket has a maximum allowed loss expressed in money (for example, a certain amount in USD based on the account size). If the total floating loss of the basket reaches this value, the entire basket is closed and the cycle ends.",
    basketP2:
      "On top of that, the account has a global equity stop: if the unrealized loss of all open positions brings the equity below a certain threshold, the bot closes trades and stops operating. This is similar to the maximum loss rules used in many prop firms.",

    dailyTitle: 'Daily target and "cool down" logic',
    dailyP1:
      "The strategy can also work with a daily profit target. Conceptually:",
    dailyBullets: [
      "At the beginning of the trading day, the bot saves the starting balance.",
      "It computes a level like starting balance + daily target amount.",
      "Once the equity reaches or exceeds that level, the bot can reduce or stop trading for the rest of the day.",
    ],
    dailyP2:
      "The daily counters and limits are then reset at a specific time (for example a few minutes after midnight server time), so the next trading day starts with a clean slate.",

    whyTitle: "Why this risk framework matters",
    whyP1:
      "A grid without limits is extremely dangerous. By combining:",
    whyBullets: [
      "A maximum number of trades in the basket,",
      "A cap on total volume,",
      "A monetary stop for the basket,",
      "An overall account equity stop,",
      "And daily limits/targets,",
    ],
    whyP2:
      "The strategy aims to behave like a disciplined professional system, not like a high-risk martingale. Losses are still possible and will certainly happen, but they are meant to be bounded and controlled.",

    riskBadge: "Important risk disclosure",
    riskText:
      "Trading on leveraged financial markets is highly risky and can lead to the loss of all your capital. Grid strategies, even when protected by risk limits, can suffer prolonged drawdowns and large losses in extreme market conditions. The information on this page is educational and does not represent personalised investment advice or a guarantee of future results. You are fully responsible for your investment decisions and for the capital you decide to allocate.",
    riskFooter:
      'Our goal with Candy Bot is to make the logic transparent, not to sell "easy money". Understanding how the strategy works is the first step to using it in a responsible way.',

    footerLine:
      "© 2024–2025 Sweet Trading Inc. · This page is for educational purposes only and does not constitute investment advice.",
  },

  it: {
    codeLabel: "IT",
    headerSubtitle: "Sweet Trading · Strategia",
    langButtonLabel: "Lingua",
    backLabel: "Indietro",

    heroBadge: "Analisi della strategia",
    heroTitle:
      "Come opera Candy Bot — spiegazione semplice di un sistema complesso",
    heroIntro:
      "Candy Bot nasce da un’idea chiara: automatizzare una strategia tecnica e circondarla di regole di rischio molto rigide. In questa pagina spieghiamo, con parole semplici, come funziona la logica principale, come si comporta la griglia e quali protezioni vengono usate per tenere il rischio sotto controllo.",
    heroChips: [
      "Ingressi basati su indicatori",
      "Griglia controllata",
      "Regole di rischio stile prop firm",
    ],

    sideBadge: "Profilo in sintesi",
    sideTitle: "TRIX + Grid con Risk Framework",
    sideP1:
      "La strategia combina un segnale di ingresso basato sul TRIX con una griglia piccola e limitata, usata per migliorare il prezzo medio quando il mercato si muove temporaneamente contro la posizione iniziale. Il tutto è inserito in regole fisse ispirate al mondo delle prop firm (limiti di equity, limiti giornalieri, stop globali).",
    sideP2:
      "Numeri ed esempi qui riportati sono illustrativi e semplificati. La configurazione reale può essere modificata e ottimizzata nel tempo.",

    s1Title: "1. Logica di base: TRIX + Griglia",
    s1Intro:
      "Al centro della strategia c’è una struttura semplice: l’indicatore TRIX decide la direzione, e una piccola griglia gestisce la posizione se il prezzo si muove temporaneamente in senso opposto.",

    trixTitle: "TRIX: il segnale principale di ingresso",
    trixP1:
      "TRIX è un indicatore tecnico che misura quanto velocemente si muove il mercato e in che direzione. In modo molto pratico:",
    trixBullets: [
      "Quando il TRIX è chiaramente positivo, il bot considera il mercato rialzista e prepara ingressi in buy.",
      "Quando il TRIX è chiaramente negativo, il bot considera il mercato ribassista e prepara ingressi in sell.",
    ],
    trixP2:
      "La strategia apre una nuova posizione solo quando non ci sono altri trade aperti e il valore del TRIX supera delle soglie precise, in modo da filtrare il semplice rumore di mercato.",

    gridTitle: "La griglia: mediazione controllata, non martingala infinita",
    gridP1:
      'Dopo il primo trade, il bot può creare un "cestello" di posizioni, cioè una griglia. L’idea è:',
    gridBullets: [
      "Partire con un volume minimo (ad esempio 0.01 lotti o simili, in base alla dimensione del conto).",
      "Se il prezzo va contro la posizione di una certa distanza predefinita, il bot può aggiungere un nuovo trade per migliorare il prezzo medio.",
      "La distanza tra un livello di griglia e l’altro dipende da una distanza minima in pips unita a filtri di volatilità (per esempio usando l’ATR per evitare posizioni troppo vicine in mercati molto tranquilli).",
      "Il volume può aumentare leggermente da un livello al successivo (moltiplicatore < 2), ma esiste un limite massimo sul numero di posizioni che la griglia può aprire.",
    ],
    gridP2:
      "Il cestello ha un proprio take profit: quando il profitto totale flottante dell’intera griglia raggiunge un certo valore prefissato (calcolato anche in base al volume totale), tutte le posizioni vengono chiuse insieme.",

    safetyTitle: "Filtri di sicurezza prima di aggiungere nuovi trade",
    safetyP1:
      "Il bot non aggiunge posizioni in modo cieco. Prima di aprire un nuovo trade nella griglia controlla diverse condizioni, ad esempio:",
    safetyBullets: [
      "Un tempo minimo trascorso dall’ultimo ingresso, per evitare che i trade vengano empilati troppo velocemente.",
      "Il prezzo deve essersi mosso a sfavore abbastanza (in base alla distanza di griglia definita).",
      "Controlli su spike di volatilità (per esempio usando l’ATR): se la candela è troppo grande, il bot può decidere di aspettare prima di aggiungere un nuovo trade.",
    ],
    safetyP2:
      "Se le condizioni non sono ottimali, il bot semplicemente aspetta. L’obiettivo è mantenere la griglia relativamente piccola e controllata, non inseguire ogni minimo movimento del prezzo.",

    s2Title: "2. Gestione del rischio: stile prop firm",
    s2Intro:
      "La strategia è pensata per comportarsi come un trader professionale in una prop firm: esistono limiti chiari che non possono essere superati. Questi limiti contano più del singolo trade o del singolo giorno.",

    basketTitle: "Stop di cestello & stop di equity",
    basketP1:
      "Ogni cestello di griglia ha una perdita massima consentita, espressa in denaro (per esempio un certo importo in base alla dimensione del conto). Se la perdita totale flottante del cestello raggiunge questo livello, l’intera griglia viene chiusa.",
    basketP2:
      "In aggiunta, il conto ha uno stop di equity globale: se la perdita non ancora realizzata di tutte le posizioni aperte porta l’equity sotto una certa soglia, il bot chiude i trade e sospende l’operatività. È un comportamento simile alle regole di perdita massima delle prop firm.",

    dailyTitle: 'Target giornaliero e logica di "raffreddamento"',
    dailyP1:
      "La strategia può essere affiancata anche da un target di profitto giornaliero. In pratica:",
    dailyBullets: [
      "All’inizio della giornata di trading, il bot salva il saldo iniziale.",
      "Calcola un livello come saldo iniziale + obiettivo di profitto giornaliero.",
      "Una volta che l’equity raggiunge o supera quel livello, il bot può ridurre l’attività o fermarsi per il resto della giornata.",
    ],
    dailyP2:
      "Contatori e limiti giornalieri vengono poi azzerati a un orario preciso (per esempio poco dopo la mezzanotte del server), così il giorno successivo inizia da una base pulita.",

    whyTitle: "Perché questo framework di rischio è importante",
    whyP1:
      "Una griglia senza limiti è estremamente pericolosa. Mettendo insieme:",
    whyBullets: [
      "Un numero massimo di trade nel cestello,",
      "Un tetto al volume complessivo,",
      "Uno stop monetario di cestello,",
      "Uno stop di equity sul conto,",
      "E limiti/obiettivi giornalieri,",
    ],
    whyP2:
      "La strategia punta a comportarsi come un sistema disciplinato e professionale, non come una martingala ad alto rischio. Le perdite sono comunque possibili e certe nel tempo, ma devono rimanere entro confini chiari e controllati.",

    riskBadge: "Avvertenza importante sul rischio",
    riskText:
      "Il trading su mercati finanziari con leva è altamente rischioso e può portare alla perdita dell’intero capitale. Le strategie a griglia, anche se protette da limiti di rischio, possono subire lunghi periodi di drawdown e perdite elevate in condizioni di mercato estreme. Le informazioni presenti in questa pagina hanno solo scopo educativo e non costituiscono consulenza personalizzata o garanzia di risultati futuri. Rimani l’unico responsabile delle tue decisioni di investimento e del capitale che decidi di allocare.",
    riskFooter:
      'Il nostro obiettivo con Candy Bot è rendere la logica trasparente, non vendere la promessa di "soldi facili". Capire come funziona la strategia è il primo passo per usarla in modo responsabile.',

    footerLine:
      "© 2024–2025 Sweet Trading Inc. · Pagina a solo scopo informativo/educativo, non costituisce consulenza finanziaria.",
  },

  fr: {
    codeLabel: "FR",
    headerSubtitle: "Sweet Trading · Stratégie",
    langButtonLabel: "Langue",
    backLabel: "Retour",

    heroBadge: "Analyse de la stratégie",
    heroTitle:
      "Comment Candy Bot trade — explication simple d’un système complexe",
    heroIntro:
      "Candy Bot repose sur une idée claire : automatiser une stratégie technique et l’entourer de règles de risque strictes. Sur cette page, nous expliquons avec des mots simples comment fonctionne le cœur de la logique, comment la grille se comporte et quelles protections sont utilisées pour garder le risque sous contrôle.",
    heroChips: [
      "Entrées basées sur indicateurs",
      "Grille contrôlée",
      "Règles de risque type prop firm",
    ],

    sideBadge: "Profil en bref",
    sideTitle: "TRIX + Grid avec cadre de risque",
    sideP1:
      "La stratégie combine un signal d’entrée basé sur le TRIX avec une petite grille limitée, utilisée pour améliorer le prix moyen lorsque le marché se déplace temporairement contre la position initiale. Le tout est enveloppé dans un ensemble de règles fixes inspirées des prop firms (limites d’equity, limites journalières, stop global).",
    sideP2:
      "Les chiffres et exemples présentés ici sont illustratifs et volontairement simplifiés. La configuration réelle peut être ajustée et optimisée dans le temps.",

    s1Title: "1. Logique de base : TRIX + Grille",
    s1Intro:
      "Au cœur de la stratégie, on trouve une structure simple : l’indicateur TRIX décide de la direction, et une petite grille gère la position si le prix se déplace temporairement dans le sens opposé.",

    trixTitle: "TRIX : principal signal d’entrée",
    trixP1:
      "TRIX est un indicateur technique qui mesure la vitesse et la direction du mouvement du marché. De façon pratique :",
    trixBullets: [
      "Quand le TRIX est clairement positif, le bot considère le marché haussier et prépare des entrées en achat.",
      "Quand le TRIX est clairement négatif, le bot considère le marché baissier et prépare des entrées en vente.",
    ],
    trixP2:
      "La stratégie n’ouvre une nouvelle position que lorsqu’aucun autre trade n’est ouvert et que la valeur du TRIX dépasse certains seuils, afin de filtrer le simple bruit de marché.",

    gridTitle: "La grille : moyenne contrôlée, pas martingale infinie",
    gridP1:
      'Après le premier trade, le bot peut créer un « panier » de positions, c’est-à-dire une grille. L’idée est :',
    gridBullets: [
      "Commencer avec un volume de base très faible (par exemple 0,01 lot, selon la taille du compte).",
      "Si le prix va contre la position d’une distance prédéfinie, le bot peut ajouter une nouvelle position pour améliorer le prix moyen.",
      "La distance entre deux niveaux de grille dépend d’une distance minimale en pips et de filtres de volatilité (par exemple en utilisant l’ATR pour éviter des entrées trop proches en marché calme).",
      "Le volume peut augmenter légèrement d’un niveau au suivant (multiplicateur < 2), mais un nombre maximum de positions dans la grille est défini.",
    ],
    gridP2:
      "Le panier possède son propre take profit : lorsque le profit flottant total de la grille atteint une valeur prédéfinie (également liée au volume total), toutes les positions sont fermées ensemble.",

    safetyTitle: "Filtres de sécurité avant chaque nouvelle position",
    safetyP1:
      "Le bot n’ajoute pas de positions de manière aveugle. Avant d’ouvrir une nouvelle position dans la grille, il vérifie plusieurs conditions, par exemple :",
    safetyBullets: [
      "Un délai minimum depuis le dernier trade, pour éviter d’empiler les positions trop vite.",
      "Le prix doit s’être déplacé suffisamment dans le sens opposé (en fonction de la distance de grille définie).",
      "Des filtres sur les pics de volatilité (par exemple via l’ATR) : si la bougie est trop grande, le bot peut décider d’attendre avant d’ajouter une nouvelle position.",
    ],
    safetyP2:
      "Si les conditions ne sont pas réunies, le bot attend simplement. L’objectif est de garder la grille relativement petite et contrôlée, et non de suivre chaque micro-mouvement du prix.",

    s2Title: "2. Gestion du risque : style prop firm",
    s2Intro:
      "La stratégie est conçue pour se comporter comme un trader professionnel dans une prop firm : il existe des limites claires qui ne peuvent pas être dépassées. Ces limites comptent plus que le trade individuel ou la journée isolée.",

    basketTitle: "Stop de panier & stop d’equity",
    basketP1:
      "Chaque panier de grille possède une perte maximale autorisée, exprimée en monnaie (par exemple un certain montant en fonction de la taille du compte). Si la perte flottante totale du panier atteint ce niveau, toute la grille est fermée.",
    basketP2:
      "En plus, le compte dispose d’un stop global d’equity : si la perte non réalisée sur l’ensemble des positions ouvertes fait passer l’equity sous un certain seuil, le bot ferme les positions et arrête l’activité. C’est similaire aux règles de perte maximale dans les prop firms.",

    dailyTitle: 'Objectif journalier et logique de "cool-down"',
    dailyP1:
      "La stratégie peut aussi fonctionner avec un objectif de profit journalier. Concrètement :",
    dailyBullets: [
      "Au début de la journée de trading, le bot enregistre le solde de départ.",
      "Il calcule un niveau équivalent au solde de départ + l’objectif journalier.",
      "Une fois que l’equity atteint ou dépasse ce niveau, le bot peut réduire son activité ou s’arrêter pour le reste de la journée.",
    ],
    dailyP2:
      "Les compteurs et limites journaliers sont ensuite réinitialisés à une heure précise (par exemple peu après minuit, heure serveur), afin que la nouvelle journée commence avec une base propre.",

    whyTitle: "Pourquoi ce cadre de risque est essentiel",
    whyP1:
      "Une grille sans limites est extrêmement dangereuse. En combinant :",
    whyBullets: [
      "Un nombre maximum de positions dans le panier,",
      "Un plafond sur le volume total,",
      "Un stop monétaire au niveau du panier,",
      "Un stop global d’equity sur le compte,",
      "Et des limites/ objectifs journaliers,",
    ],
    whyP2:
      "La stratégie cherche à se comporter comme un système discipliné et professionnel, et non comme une martingale à très haut risque. Les pertes restent possibles et feront partie du parcours, mais elles doivent rester dans des limites claires et contrôlées.",

    riskBadge: "Avertissement de risque important",
    riskText:
      "Le trading sur des marchés financiers à effet de levier est très risqué et peut entraîner la perte de la totalité de votre capital. Les stratégies de type grille, même encadrées par des limites de risque, peuvent subir de longues phases de drawdown et des pertes importantes en conditions de marché extrêmes. Les informations présentées sur cette page sont uniquement éducatives et ne constituent pas un conseil personnalisé ni une garantie de résultats futurs. Vous restez seul responsable de vos décisions d’investissement et du capital que vous choisissez d’engager.",
    riskFooter:
      'Notre objectif avec Candy Bot est de rendre la logique transparente, pas de vendre la promesse de "gains faciles". Comprendre comment la stratégie fonctionne est la première étape pour l’utiliser de manière responsable.',

    footerLine:
      "© 2024–2025 Sweet Trading Inc. · Page à but purement éducatif et informatif, ne constitue pas un conseil financier.",
  },

  de: {
    codeLabel: "DE",
    headerSubtitle: "Sweet Trading · Strategie",
    langButtonLabel: "Sprache",
    backLabel: "Zurück",

    heroBadge: "Strategie-Überblick",
    heroTitle:
      "Wie Candy Bot handelt – einfache Erklärung eines komplexen Systems",
    heroIntro:
      "Candy Bot basiert auf einer klaren Idee: eine technische Strategie zu automatisieren und mit strengen Risikoregeln zu umgeben. Auf dieser Seite erklären wir in einfachen Worten, wie die Kernlogik funktioniert, wie sich das Grid verhält und welche Schutzmechanismen eingesetzt werden, um das Risiko zu kontrollieren.",
    heroChips: [
      "Indikatorbasierte Einstiege",
      "Kontrolliertes Grid",
      "Risikoregeln im Prop-Firm-Stil",
    ],

    sideBadge: "Profil im Überblick",
    sideTitle: "TRIX + Grid mit Risiko-Framework",
    sideP1:
      "Die Strategie kombiniert ein Einstiegssignal auf Basis des TRIX-Indikators mit einem kleinen, begrenzten Grid, das dazu dient, den Durchschnittskurs zu verbessern, wenn sich der Markt vorübergehend gegen die Erstposition bewegt. Das Ganze ist eingebettet in feste Regeln, die sich an Prop-Firm-Vorgaben orientieren (Equity-Limits, Tageslimits, globale Stops).",
    sideP2:
      "Die hier gezeigten Zahlen und Beispiele sind bewusst vereinfacht und dienen nur zur Veranschaulichung. Die reale Konfiguration kann im Laufe der Zeit angepasst und optimiert werden.",

    s1Title: "1. Kernlogik: TRIX + Grid",
    s1Intro:
      "Im Zentrum der Strategie steht eine einfache Struktur: Der TRIX-Indikator bestimmt die Richtung, und ein kleines Grid verwaltet die Position, wenn sich der Preis kurzfristig in die Gegenrichtung bewegt.",

    trixTitle: "TRIX: das wichtigste Einstiegssignal",
    trixP1:
      "TRIX ist ein technischer Indikator, der misst, wie schnell und in welche Richtung sich der Markt bewegt. Ganz praktisch:",
    trixBullets: [
      "Wenn der TRIX deutlich positiv ist, betrachtet der Bot den Markt als bullisch und bereitet Long-Einstiege vor.",
      "Wenn der TRIX deutlich negativ ist, betrachtet der Bot den Markt als bärisch und bereitet Short-Einstiege vor.",
    ],
    trixP2:
      "Die Strategie eröffnet nur dann eine neue Position, wenn keine andere Position offen ist und der TRIX festgelegte Schwellenwerte überschreitet. So wird bloßer Marktrauschen gefiltert.",

    gridTitle: "Das Grid: kontrolliertes Averaging, keine endlose Martingale",
    gridP1:
      'Nach dem ersten Trade kann der Bot einen "Basket" aus mehreren Positionen aufbauen – ein Grid. Die Idee:',
    gridBullets: [
      "Start mit einem sehr kleinen Basisvolumen (z. B. 0,01 Lot, abhängig von der Kontogröße).",
      "Bewegt sich der Preis eine definierte Distanz gegen die Position, kann der Bot eine weitere Position hinzufügen, um den durchschnittlichen Einstiegskurs zu verbessern.",
      "Der Abstand zwischen zwei Grid-Levels basiert auf einem minimalen Pip-Abstand plus Volatilitätsfiltern (z. B. mithilfe des ATR, um zu enge Einstiege in ruhigen Märkten zu vermeiden).",
      "Das Volumen kann von Level zu Level leicht steigen (Multiplikator < 2), aber es gibt eine feste Obergrenze für die Anzahl der Positionen im Basket.",
    ],
    gridP2:
      "Der Basket hat ein eigenes Take-Profit-Ziel: Sobald der gesamte schwebende Gewinn des Grids einen vordefinierten Wert erreicht (abhängig u. a. vom Gesamtvolumen), werden alle Positionen gemeinsam geschlossen.",

    safetyTitle: "Sicherheitsfilter vor jedem neuen Trade",
    safetyP1:
      "Der Bot fügt keine Positionen blind hinzu. Vor jeder neuen Position im Grid werden mehrere Bedingungen geprüft, zum Beispiel:",
    safetyBullets: [
      "Eine Mindestzeitspanne seit dem letzten Einstieg, damit nicht zu schnell hintereinander gehandelt wird.",
      "Der Preis muss sich ausreichend stark in die Gegenrichtung bewegt haben (gemessen an der definierten Grid-Distanz).",
      "Filter für Volatilitätsspitzen (z. B. auf Basis des ATR): Ist die Kerze extrem groß, kann der Bot mit einem neuen Einstieg warten.",
    ],
    safetyP2:
      "Sind die Bedingungen nicht erfüllt, wartet der Bot einfach. Ziel ist es, das Grid relativ klein und kontrolliert zu halten – nicht jedem Mini-Bewegung hinterherzulaufen.",

    s2Title: "2. Risikomanagement: angelehnt an Prop-Firm-Regeln",
    s2Intro:
      "Die Strategie soll sich verhalten wie ein professioneller Trader in einer Prop-Firm: Es gibt klar definierte Grenzen, die nicht überschritten werden dürfen. Diese Grenzen sind wichtiger als der einzelne Trade oder der einzelne Tag.",

    basketTitle: "Basket-Stop & Equity-Stop",
    basketP1:
      "Jeder Grid-Basket hat einen maximal erlaubten Verlust, ausgedrückt in Geld (z. B. ein bestimmter Betrag in Abhängigkeit von der Kontogröße). Erreicht der gesamte schwebende Verlust dieses Niveaus, wird der gesamte Basket geschlossen.",
    basketP2:
      "Zusätzlich gibt es einen globalen Equity-Stop für das Konto: Fällt die Equity durch nicht realisierte Verluste aller offenen Positionen unter einen gewissen Schwellenwert, schließt der Bot die Trades und stoppt die Aktivität. Das ähnelt den Max-Loss-Regeln vieler Prop-Firms.",

    dailyTitle: 'Tagesziel und "Cool-Down"-Logik',
    dailyP1:
      "Die Strategie kann mit einem täglichen Gewinnziel kombiniert werden. Grundidee:",
    dailyBullets: [
      "Zu Beginn des Trading-Tages speichert der Bot den Startkontostand.",
      "Er berechnet ein Niveau wie Startkontostand + Tagesziel.",
      "Sobald die Equity dieses Niveau erreicht oder überschreitet, kann der Bot die Aktivität reduzieren oder den Handel für den Rest des Tages einstellen.",
    ],
    dailyP2:
      "Tagesbezogene Zähler und Limits werden dann zu einem festen Zeitpunkt zurückgesetzt (z. B. kurz nach Mitternacht, Serverzeit), sodass der neue Tag mit einer sauberen Basis startet.",

    whyTitle: "Warum dieses Risiko-Framework wichtig ist",
    whyP1:
      "Ein Grid ohne Grenzen ist extrem gefährlich. Durch die Kombination von:",
    whyBullets: [
      "Maximaler Anzahl an Positionen im Basket,",
      "Obergrenze für das Gesamtvolumen,",
      "Monetärem Basket-Stop,",
      "Globalem Equity-Stop auf Kontoebene,",
      "Und täglichen Limits/Zielen,",
    ],
    whyP2:
      "soll sich die Strategie wie ein diszipliniertes, professionelles System verhalten – nicht wie eine hochriskante Martingale. Verluste sind weiterhin möglich und werden auftreten, sollen aber innerhalb klarer und kontrollierter Grenzen bleiben.",

    riskBadge: "Wichtiger Risikohinweis",
    riskText:
      "Der Handel an gehebelten Finanzmärkten ist mit sehr hohen Risiken verbunden und kann zum Verlust des gesamten eingesetzten Kapitals führen. Grid-Strategien können – selbst mit Risikobegrenzungen – lange Drawdown-Phasen und hohe Verluste in extremen Marktsituationen erleiden. Die Informationen auf dieser Seite dienen ausschließlich Bildungszwecken und stellen keine individuelle Anlageberatung oder Garantie zukünftiger Ergebnisse dar. Sie tragen die alleinige Verantwortung für Ihre Investmententscheidungen und das Kapital, das Sie einsetzen.",
    riskFooter:
      'Unser Ziel mit Candy Bot ist es, die Logik transparent zu machen – nicht, "leichtes Geld" zu versprechen. Zu verstehen, wie die Strategie funktioniert, ist der erste Schritt, um sie verantwortungsvoll zu nutzen.',

    footerLine:
      "© 2024–2025 Sweet Trading Inc. · Seite nur zu Informations- und Bildungszwecken, keine Finanzberatung.",
  },
};

/* -------------------------- Helpers / hooks -------------------------- */
function useOutsideClick(ref, handler, when = true) {
  React.useEffect(() => {
    if (!when) return;

    const onDown = (e) => {
      const el = ref?.current;
      if (!el) return;
      if (el.contains(e.target)) return;
      handler?.(e);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });

    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [ref, handler, when]);
}

function cx(...classes) {
  return classes.filter(Boolean).join(" ");
}

function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  el.scrollIntoView({ behavior: "smooth", block: "start" });
}

const reveal = (reduce) => ({
  initial: reduce ? { opacity: 1, y: 0 } : { opacity: 0, y: 18 },
  whileInView: reduce ? { opacity: 1, y: 0 } : { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-120px 0px -120px 0px" },
  transition: { duration: 0.55, ease: "easeOut" },
});

/* ------------------------------ Component ------------------------------ */
export default function StrategyPage() {
  const navigate = useNavigate();
  const reduceMotion = useReducedMotion();

  const [showLangMenu, setShowLangMenu] = React.useState(false);
  const [lang, setLang] = React.useState(() => {
    try {
      return localStorage.getItem("st_strategy_lang") || "en";
    } catch {
      return "en";
    }
  });

  const copy = React.useMemo(
    () => STRATEGY_COPY[lang] || STRATEGY_COPY.en,
    [lang]
  );

  const languages = React.useMemo(
    () => [
      { code: "en", label: "EN", flag: "🇬🇧" },
      { code: "it", label: "IT", flag: "🇮🇹" },
      { code: "fr", label: "FR", flag: "🇫🇷" },
      { code: "de", label: "DE", flag: "🇩🇪" },
    ],
    []
  );

  // Sticky header “scrolled shadow”
  const [scrolled, setScrolled] = React.useState(false);
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Persist language
  React.useEffect(() => {
    try {
      localStorage.setItem("st_strategy_lang", lang);
    } catch {}
  }, [lang]);

  // Close lang menu on ESC
  React.useEffect(() => {
    if (!showLangMenu) return;
    const onKey = (e) => {
      if (e.key === "Escape") setShowLangMenu(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showLangMenu]);

  // Outside click for lang menu
  const langMenuRef = React.useRef(null);
  useOutsideClick(langMenuRef, () => setShowLangMenu(false), showLangMenu);

  // Scroll progress bar
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, {
    stiffness: 140,
    damping: 28,
    mass: 0.25,
  });

  // Back to top visibility
  const [showTop, setShowTop] = React.useState(false);
  React.useEffect(() => {
    const unsub = scrollYProgress.on("change", (v) => setShowTop(v > 0.12));
    return () => unsub?.();
  }, [scrollYProgress]);

  // Scroll-spy
  const [activeId, setActiveId] = React.useState("core");
  React.useEffect(() => {
    const ids = ["core", "risk", "disclaimer"];
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length) return;

    const obs = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort(
            (a, b) =>
              (b.intersectionRatio || 0) - (a.intersectionRatio || 0)
          );
        if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: [0.08, 0.18, 0.35] }
    );

    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [lang]);

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/");
  };

  const toc = React.useMemo(
    () => [
      { id: "core", label: copy.s1Title, icon: "①" },
      { id: "risk", label: copy.s2Title, icon: "②" },
      { id: "disclaimer", label: copy.riskBadge, icon: "⚠️" },
    ],
    [copy]
  );

  return (
    <div
      id="top"
      className="min-h-screen text-slate-100 px-3 sm:px-4 py-4 sm:py-6 relative overflow-hidden"
    >
      {/* Premium background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950" />
      <div className="pointer-events-none absolute inset-0 opacity-[0.65]">
        <div className="absolute -top-24 -left-20 w-72 h-72 bg-indigo-500/20 blur-3xl" />
        <div className="absolute top-40 -right-24 w-80 h-80 bg-fuchsia-500/12 blur-3xl" />
        <div className="absolute bottom-[-120px] left-1/3 w-[520px] h-[520px] bg-sky-500/10 blur-3xl" />
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgba(148,163,184,0.08) 1px, transparent 0)",
            backgroundSize: "28px 28px",
            maskImage:
              "radial-gradient(ellipse at center, rgba(0,0,0,1) 45%, rgba(0,0,0,0) 78%)",
          }}
        />
      </div>

      {/* Scroll progress */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-[2px] z-50 origin-left bg-gradient-to-r from-indigo-400 via-sky-300 to-fuchsia-300"
        style={{ scaleX: progress }}
      />

      <div className="max-w-5xl mx-auto relative">
        {/* HEADER */}
        <motion.header
          initial={reduceMotion ? false : { opacity: 0, y: -10 }}
          animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className={cx(
            "flex items-center justify-between gap-3 border-b pb-2 sm:pb-3 sticky top-0 z-20",
            "backdrop-blur-md",
            scrolled
              ? "bg-slate-950/85 border-slate-700/70 shadow-[0_10px_35px_rgba(0,0,0,0.35)]"
              : "bg-slate-950/65 border-slate-700/60"
          )}
        >
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="inline-flex items-center gap-2">
              <img
                src="/stt1.png"
                alt="Sweet Trading Logo"
                className="h-7 sm:h-8 w-auto drop-shadow"
              />
              <span className="hidden sm:inline text-[0.7rem] tracking-[0.18em] uppercase text-indigo-200/90">
                {copy.headerSubtitle}
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2 sm:gap-3">
            {/* LANGUAGE SWITCHER */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setShowLangMenu((v) => !v)}
                className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-slate-500/80 bg-slate-900/70 text-[0.7rem] sm:text-xs text-slate-100 hover:border-indigo-400 hover:text-indigo-100 transition-colors"
                aria-label="Select language"
                aria-expanded={showLangMenu}
              >
                <span className="text-sm sm:text-base leading-none">🌐</span>
                <span className="hidden xs:inline font-semibold">
                  {copy.codeLabel}
                </span>
                <span className="hidden sm:inline ml-1">
                  {copy.langButtonLabel}
                </span>
              </button>

              <AnimatePresence>
                {showLangMenu && (
                  <motion.div
                    initial={
                      reduceMotion
                        ? { opacity: 1 }
                        : { opacity: 0, y: -8, scale: 0.98 }
                    }
                    animate={
                      reduceMotion
                        ? { opacity: 1 }
                        : { opacity: 1, y: 0, scale: 1 }
                    }
                    exit={
                      reduceMotion
                        ? { opacity: 0 }
                        : { opacity: 0, y: -8, scale: 0.98 }
                    }
                    transition={{ duration: 0.18, ease: "easeOut" }}
                    className="absolute right-0 mt-1.5 w-32 bg-slate-900/95 border border-slate-700 rounded-md shadow-lg overflow-hidden text-xs z-30"
                    role="menu"
                  >
                    {languages.map((l) => (
                      <button
                        key={l.code}
                        onClick={() => {
                          setLang(l.code);
                          setShowLangMenu(false);
                        }}
                        className={cx(
                          "w-full flex items-center gap-2 px-3 py-2 text-left transition-colors",
                          l.code === lang
                            ? "bg-slate-800 text-indigo-200"
                            : "text-slate-100 hover:bg-slate-800"
                        )}
                        role="menuitem"
                      >
                        <span className="text-base leading-none">{l.flag}</span>
                        <span className="font-semibold">{l.label}</span>
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* BACK BUTTON */}
            <button
              onClick={handleBack}
              className="inline-flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 text-[0.7rem] sm:text-xs rounded-full border border-slate-500/80 bg-slate-900/70 hover:border-indigo-400 hover:text-indigo-100 transition-colors"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-3.5 h-3.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              <span className="hidden xs:inline">{copy.backLabel}</span>
            </button>
          </div>
        </motion.header>

        {/* MAIN */}
        <main className="mt-4 sm:mt-6 space-y-6 sm:space-y-8">
          {/* HERO / OVERVIEW */}
          <section className="grid gap-4 sm:gap-6 md:grid-cols-[1.8fr,1.2fr] items-start">
            <motion.div {...reveal(reduceMotion)}>
              <p className="inline-flex items-center text-[0.6rem] sm:text-[0.65rem] uppercase tracking-[0.24em] text-indigo-200 bg-indigo-500/10 border border-indigo-500/40 rounded-full px-2.5 sm:px-3 py-1 mb-2.5 sm:mb-3">
                {copy.heroBadge}
              </p>

              <h1 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold leading-tight mb-2.5 sm:mb-3 bg-gradient-to-r from-slate-50 via-slate-100 to-indigo-300 bg-clip-text text-transparent">
                {copy.heroTitle}
              </h1>

              <p className="text-[0.8rem] sm:text-sm md:text-base text-slate-300/90 leading-relaxed">
                {copy.heroIntro}
              </p>

              <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-3 sm:mt-4">
                {copy.heroChips.map((chip, i) => (
                  <motion.span
                    key={i}
                    initial={
                      reduceMotion ? { opacity: 1 } : { opacity: 0, y: 10 }
                    }
                    animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.35,
                      ease: "easeOut",
                      delay: 0.08 + i * 0.06,
                    }}
                    className="text-[0.65rem] sm:text-[0.7rem] uppercase tracking-[0.18em] px-2.5 py-1 rounded-full border border-slate-500/80 bg-slate-900/70 text-slate-100 hover:border-indigo-400/80 transition-colors"
                  >
                    {chip}
                  </motion.span>
                ))}
              </div>
            </motion.div>

            {/* RIGHT COLUMN */}
            <div className="space-y-3 sm:space-y-4">
              {/* SIDE CARD */}
              <motion.div
                {...reveal(reduceMotion)}
                transition={{ duration: 0.55, ease: "easeOut", delay: 0.06 }}
                whileHover={reduceMotion ? {} : { y: -3 }}
                className="relative rounded-2xl border border-indigo-500/40 bg-slate-900/80 shadow-2xl overflow-hidden"
              >
                <div className="absolute -top-20 -left-10 w-32 sm:w-40 h-32 sm:h-40 bg-indigo-500/20 blur-3xl" />
                <div className="absolute -bottom-20 right-0 w-40 sm:w-52 h-40 sm:h-52 bg-pink-500/10 blur-3xl" />
                <div className="relative p-3 sm:p-4 md:p-5 space-y-2.5 sm:space-y-3">
                  <p className="text-[0.6rem] sm:text-[0.65rem] uppercase tracking-[0.22em] text-indigo-200">
                    {copy.sideBadge}
                  </p>
                  <p className="text-base sm:text-lg md:text-xl font-semibold text-slate-50">
                    {copy.sideTitle}
                  </p>
                  <p className="text-[0.75rem] sm:text-xs text-slate-300/90">
                    {copy.sideP1}
                  </p>
                  <p className="text-[0.7rem] text-slate-400 leading-relaxed">
                    {copy.sideP2}
                  </p>
                </div>
              </motion.div>

              {/* TOC (desktop sticky) */}
              <div className="hidden md:block">
                <motion.div
                  {...reveal(reduceMotion)}
                  transition={{ duration: 0.55, ease: "easeOut", delay: 0.1 }}
                  className="sticky top-[84px] rounded-2xl border border-slate-700/70 bg-slate-950/65 backdrop-blur-md shadow-xl overflow-hidden"
                >
                  <div className="px-4 py-3 border-b border-slate-700/60">
                    <p className="text-[0.7rem] uppercase tracking-[0.22em] text-slate-300">
                      Contents
                    </p>
                  </div>
                  <div className="p-2">
                    {toc.map((t) => {
                      const active = activeId === t.id;
                      return (
                        <button
                          key={t.id}
                          onClick={() => scrollToId(t.id)}
                          className={cx(
                            "w-full flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-colors",
                            active
                              ? "bg-indigo-500/10 border border-indigo-400/30 text-indigo-100"
                              : "hover:bg-slate-900/50 text-slate-200"
                          )}
                          aria-current={active ? "page" : undefined}
                        >
                          <span className="text-sm">{t.icon}</span>
                          <span className="text-[0.78rem] font-semibold">
                            {t.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* SECTION 1 – CORE LOGIC */}
          <motion.section
            id="core"
            {...reveal(reduceMotion)}
            className="relative rounded-2xl border border-slate-700/80 bg-slate-950/70 backdrop-blur-md shadow-xl p-3.5 sm:p-5 space-y-4 sm:space-y-5"
          >
            <div>
              <h2 className="flex items-center gap-2 text-base sm:text-lg font-semibold">
                <span className="w-2 h-2 rounded-full bg-fuchsia-500 shadow-[0_0_16px_rgba(217,70,239,0.8)]" />
                {copy.s1Title}
              </h2>
              <p className="text-[0.8rem] sm:text-sm text-slate-300/90 mt-1.5">
                {copy.s1Intro}
              </p>
            </div>

            <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
              {/* TRIX */}
              <motion.div
                whileHover={reduceMotion ? {} : { y: -2 }}
                className="rounded-xl border border-slate-700 bg-slate-900/60 backdrop-blur p-3 sm:p-4 space-y-2 hover:border-indigo-400/40 transition-colors"
              >
                <p className="text-[0.75rem] sm:text-xs font-semibold text-slate-100">
                  {copy.trixTitle}
                </p>
                <p className="text-[0.75rem] sm:text-xs text-slate-300/90">
                  {copy.trixP1}
                </p>
                <ul className="space-y-1 text-[0.75rem] sm:text-xs mt-1 text-slate-200">
                  {copy.trixBullets.map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="mt-[2px] opacity-80">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[0.75rem] sm:text-xs text-slate-300/90 mt-1.5">
                  {copy.trixP2}
                </p>
              </motion.div>

              {/* GRID */}
              <motion.div
                whileHover={reduceMotion ? {} : { y: -2 }}
                className="rounded-xl border border-slate-700 bg-slate-900/60 backdrop-blur p-3 sm:p-4 space-y-2 hover:border-indigo-400/40 transition-colors"
              >
                <p className="text-[0.75rem] sm:text-xs font-semibold text-slate-100">
                  {copy.gridTitle}
                </p>
                <p className="text-[0.75rem] sm:text-xs text-slate-300/90">
                  {copy.gridP1}
                </p>
                <ul className="space-y-1 text-[0.75rem] sm:text-xs mt-1 text-slate-200">
                  {copy.gridBullets.map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="mt-[2px] opacity-80">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[0.75rem] sm:text-xs text-slate-300/90 mt-1.5">
                  {copy.gridP2}
                </p>
              </motion.div>
            </div>

            {/* SAFETY FILTERS */}
            <motion.div
              whileHover={reduceMotion ? {} : { y: -2 }}
              className="rounded-xl border border-slate-700 bg-slate-900/60 backdrop-blur p-3 sm:p-4 space-y-2 hover:border-indigo-400/40 transition-colors"
            >
              <p className="text-[0.75rem] sm:text-xs font-semibold text-slate-100">
                {copy.safetyTitle}
              </p>
              <p className="text-[0.75rem] sm:text-xs text-slate-300/90">
                {copy.safetyP1}
              </p>
              <ul className="space-y-1 text-[0.75rem] sm:text-xs text-slate-200">
                {copy.safetyBullets.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="mt-[2px] opacity-80">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[0.75rem] sm:text-xs text-slate-300/90 mt-1.5">
                {copy.safetyP2}
              </p>
            </motion.div>
          </motion.section>

          {/* SECTION 2 – RISK FRAMEWORK */}
          <motion.section
            id="risk"
            {...reveal(reduceMotion)}
            className="relative rounded-2xl border border-slate-700/80 bg-slate-950/70 backdrop-blur-md shadow-xl p-3.5 sm:p-5 space-y-4 sm:space-y-5"
          >
            <h2 className="flex items-center gap-2 text-base sm:text-lg font-semibold">
              <span className="w-2 h-2 rounded-full bg-indigo-400 shadow-[0_0_16px_rgba(129,140,248,0.9)]" />
              {copy.s2Title}
            </h2>

            <p className="text-[0.8rem] sm:text-sm text-slate-300/90">
              {copy.s2Intro}
            </p>

            <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
              {/* BASKET & EQUITY STOP */}
              <motion.div
                whileHover={reduceMotion ? {} : { y: -2 }}
                className="rounded-xl border border-slate-700 bg-slate-900/60 backdrop-blur p-3 sm:p-4 space-y-2 hover:border-indigo-400/40 transition-colors"
              >
                <p className="text-[0.75rem] sm:text-xs font-semibold text-slate-100">
                  {copy.basketTitle}
                </p>
                <p className="text-[0.75rem] sm:text-xs text-slate-300/90">
                  {copy.basketP1}
                </p>
                <p className="text-[0.75rem] sm:text-xs text-slate-300/90 mt-1">
                  {copy.basketP2}
                </p>
              </motion.div>

              {/* DAILY TARGET */}
              <motion.div
                whileHover={reduceMotion ? {} : { y: -2 }}
                className="rounded-xl border border-slate-700 bg-slate-900/60 backdrop-blur p-3 sm:p-4 space-y-2 hover:border-indigo-400/40 transition-colors"
              >
                <p className="text-[0.75rem] sm:text-xs font-semibold text-slate-100">
                  {copy.dailyTitle}
                </p>
                <p className="text-[0.75rem] sm:text-xs text-slate-300/90">
                  {copy.dailyP1}
                </p>
                <ul className="space-y-1 text-[0.75rem] sm:text-xs mt-1 text-slate-200">
                  {copy.dailyBullets.map((b, i) => (
                    <li key={i} className="flex gap-2">
                      <span className="mt-[2px] opacity-80">•</span>
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-[0.75rem] sm:text-xs text-slate-300/90 mt-1.5">
                  {copy.dailyP2}
                </p>
              </motion.div>
            </div>

            {/* WHY IT MATTERS */}
            <motion.div
              whileHover={reduceMotion ? {} : { y: -2 }}
              className="rounded-xl border border-slate-700 bg-slate-900/60 backdrop-blur p-3 sm:p-4 space-y-2 hover:border-indigo-400/40 transition-colors"
            >
              <p className="text-[0.75rem] sm:text-xs font-semibold text-slate-100">
                {copy.whyTitle}
              </p>
              <p className="text-[0.75rem] sm:text-xs text-slate-300/90">
                {copy.whyP1}
              </p>
              <ul className="space-y-1 text-[0.75rem] sm:text-xs mt-1 text-slate-200">
                {copy.whyBullets.map((b, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="mt-[2px] opacity-80">•</span>
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
              <p className="text-[0.75rem] sm:text-xs text-slate-300/90 mt-1.5">
                {copy.whyP2}
              </p>
            </motion.div>
          </motion.section>

          {/* RISK DISCLAIMER */}
          <motion.section
            id="disclaimer"
            {...reveal(reduceMotion)}
            className="space-y-3 sm:space-y-4"
          >
            <motion.div
              whileHover={reduceMotion ? {} : { y: -2 }}
              className="rounded-2xl border border-rose-500/50 bg-rose-900/10 backdrop-blur p-3 sm:p-4"
            >
              <p className="text-[0.65rem] sm:text-[0.7rem] uppercase tracking-[0.2em] text-rose-300 mb-1">
                {copy.riskBadge}
              </p>
              <p className="text-[0.7rem] sm:text-[0.75rem] text-slate-200/95 leading-relaxed">
                {copy.riskText}
              </p>
            </motion.div>

            <p className="text-[0.7rem] sm:text-xs text-slate-400/90 max-w-xl">
              {copy.riskFooter}
            </p>
          </motion.section>
        </main>

        <footer className="mt-6 sm:mt-8 text-center text-[0.65rem] sm:text-[0.7rem] text-slate-500">
          {copy.footerLine}
        </footer>
      </div>

      {/* Back to top */}
      <AnimatePresence>
        {showTop && (
          <motion.button
            initial={
              reduceMotion
                ? { opacity: 1 }
                : { opacity: 0, y: 10, scale: 0.98 }
            }
            animate={reduceMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
            exit={
              reduceMotion
                ? { opacity: 0 }
                : { opacity: 0, y: 10, scale: 0.98 }
            }
            transition={{ duration: 0.18, ease: "easeOut" }}
            onClick={() => scrollToId("top")}
            className="fixed bottom-4 right-4 z-40 rounded-full border border-slate-600/70 bg-slate-950/75 backdrop-blur px-3 py-2 text-xs text-slate-100 shadow-xl hover:border-indigo-400/70 hover:text-indigo-100 transition-colors"
            aria-label="Back to top"
          >
            ↑ Top
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
